import { useCallback, useEffect, useState } from 'react';

import { Alert, Button, Drawer, Form, Input, InputNumber, message, Modal, Space, Table, Typography } from 'antd';

import { getWebappCodes } from '../../../api/Get';
import { putWebappCodeMetadata } from '../../../api/Put';

import CodeDetail from './CodeDetail';

const { Paragraph, Text, Title } = Typography;

const formatDate = value =>
  value ? new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;

/*
 * Liste des codes de déblocage. Le détail et les modifications se font dans un panneau
 * dédié, pour ne pas charger tous les détenteurs de tous les codes d'un coup — ce que
 * faisait la version précédente, et qui ne passait plus à l'échelle.
 */
const WebappCode = () => {
  const [codes, setCodes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getWebappCodes();
    setLoading(false);
    if (res.error) setError(res.error);
    else setCodes(res.codes);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createCode = async values => {
    const clean = values.code
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^\w-]/g, '');
    if (!clean) {
      setError('Code invalide');
      return;
    }

    const res = await putWebappCodeMetadata(clean, {
      description: values.description,
      maxUsage: values.maxUsage ?? -1,
    });

    if (res.error) setError(res.error);
    else {
      messageApi.open({ content: `Code ${clean} créé`, type: 'success' });
      setCreating(false);
      form.resetFields();
      await load();
      // On enchaîne sur son détail : un code vide ne sert à rien tant qu'il n'a pas de séries.
      setSelected(clean);
    }
  };

  const columns = [
    {
      dataIndex: 'code',
      key: 'code',
      render: code => <Text strong>{code}</Text>,
      sorter: (a, b) => a.code.localeCompare(b.code),
      title: 'Code',
    },
    { dataIndex: 'description', key: 'description', title: 'Description' },
    {
      dataIndex: 'episodes',
      key: 'episodes',
      sorter: (a, b) => a.episodes - b.episodes,
      title: 'Épisodes',
    },
    {
      dataIndex: 'detenteurs',
      key: 'detenteurs',
      sorter: (a, b) => a.detenteurs - b.detenteurs,
      title: 'Détenteurs',
    },
    {
      dataIndex: 'maxUsage',
      key: 'maxUsage',
      render: n => (n === -1 || n === null ? <Text type="secondary">illimité</Text> : n),
      title: 'Usages max',
    },
    {
      dataIndex: 'endAt',
      key: 'endAt',
      render: d => formatDate(d) || <Text type="secondary">—</Text>,
      title: 'Termine le',
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: d => formatDate(d) || <Text type="secondary">—</Text>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      title: 'Créé le',
    },
    {
      dataIndex: 'updatedAt',
      // Le tri par défaut : ce qui a bougé récemment est ce qu'on vient chercher.
      defaultSortOrder: 'descend',

      key: 'updatedAt',

      render: d => formatDate(d) || <Text type="secondary">—</Text>,
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
      title: 'Modifié le',
    },
    {
      key: 'actions',
      render: (_, row) => (
        <Button size="small" type="link" onClick={() => setSelected(row.code)}>
          Gérer
        </Button>
      ),
      title: '',
    },
  ];

  return (
    <>
      {contextHolder}
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space direction="vertical" size={0}>
          <Title level={4} style={{ marginBottom: 0 }}>
            Codes Webapp
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Un code ouvre une sélection de séries aux comptes qui le détiennent. « Gérer » permet d&apos;en modifier la sélection,
            en voyant d&apos;abord ce que cela changerait pour les détenteurs.
          </Paragraph>
        </Space>

        {error && <Alert message={error} showIcon type="error" closable onClose={() => setError('')} />}

        <Space>
          <Button type="primary" onClick={() => setCreating(true)}>
            Nouveau code
          </Button>
          <Button onClick={load} loading={loading}>
            Rafraîchir
          </Button>
        </Space>

        <Table columns={columns} dataSource={codes} loading={loading} pagination={{ pageSize: 20 }} rowKey="code" size="small" />
      </Space>

      <Drawer
        open={Boolean(selected)}
        onClose={() => {
          setSelected(null);
          load();
        }}
        title={selected}
        width={960}>
        {selected && <CodeDetail code={selected} onChanged={load} />}
      </Drawer>

      <Modal
        open={creating}
        okText="Créer"
        cancelText="Annuler"
        title="Nouveau code"
        onCancel={() => setCreating(false)}
        onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={createCode}>
          <Form.Item label="Code" name="code" rules={[{ message: 'Code requis', required: true }]}>
            <Input placeholder="EDIFICE_ENSEIGNANT_2028" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input />
          </Form.Item>
          <Form.Item label="Usages max" name="maxUsage" tooltip="-1 pour illimité">
            <InputNumber defaultValue={-1} />
          </Form.Item>
        </Form>
        <Text type="secondary">Les séries s&apos;ajoutent ensuite, dans le détail du code.</Text>
      </Modal>
    </>
  );
};

export default WebappCode;
